import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { AddressImportType } from '../AddressImportTypes';
import AddressImporterResults from './AddressImporterResults';
import React, { memo, useEffect, useState } from 'react';
import { addBulkAddresses, AddressDataUpdateType, useBulkUpdateAddressData } from '../../../Api/AddressEndpoints';
import { AddressListItemType } from '../../../Types/AddressType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressBook, faKey } from '@fortawesome/pro-solid-svg-icons';
import { ColourMod } from '../../../Framework/Data/Enums/LayoutEnums';
import { useToasts } from '../../../Framework/Providers/ToastsProvider';
import { CountryCodes } from '../../../Framework/Data/CountryCodes';
import { GetObjectIndexByKeyValue } from '../../../Framework/Constants/Helpers';

type AddressImporterFormType = {
	addressData: any;
	embeddedForm?: boolean;
	savedCallback?: any;
	removeCallback?: any;
};

const AddressImporterForm = memo((props: AddressImporterFormType) => {
	const { addToast } = useToasts();

	const [addressError, setAddressError] = useState<any>();
	const [addressSaving, setAddressSaving] = useState<boolean>(false);

	const formMethods = useForm({
		mode: 'onChange',
	});

	const updateAddressData = useBulkUpdateAddressData();

	const HandleSave = (data: FieldValues) => {
		setAddressSaving(true);

		let mutateUiAddressData: Array<AddressListItemType> = [];

		data.Addresses.forEach((item: any) => {
			item['addressLine3'] =
				CountryCodes[GetObjectIndexByKeyValue(CountryCodes, 'value', item['countryCode'])].label;
		});

		addBulkAddresses(data as Array<AddressImportType>)
			.then((r: any) => {
				if (r.addresses.length === 0) {
					addToast(
						data.Addresses.length === 1 ? 'Address Already Exists' : 'Addresses Already Exist',
						<FontAwesomeIcon icon={faAddressBook} />,
						ColourMod.Alert
					);
				} else {
					r.addresses.forEach((addressResult: any) => {
						mutateUiAddressData.push({
							object: {
								addressId: addressResult.addressId,
								name: addressResult.name,
								businessName: addressResult.businessName,
								propertyNumber: addressResult.propertyNumber,
								propertyName: addressResult.propertyName,
								flatNumber: addressResult.flatNumber,
								addressLine1: addressResult.addressLine1,
								addressLine2: addressResult.addressLine2,
								addressLine3: addressResult.addressLine3,
								addressLine4: addressResult.addressLine4,
								postCode: addressResult.postCode,
								countryCode: addressResult.countryCode,
								emailAddress: addressResult.emailAddress,
								phoneNo: addressResult.phoneNo,
							},
							label: addressResult.addressString,
							value: addressResult.addressId,
						});
					});

					updateAddressData
						.mutateAsync({
							newData: mutateUiAddressData,
							updateType: AddressDataUpdateType.CREATE,
						})
						.then((r) => {
							if (props.savedCallback) {
								props.savedCallback(mutateUiAddressData);
							}
						});

					addToast(
						`Imported ${r.addresses.length} ${r.addresses.length === 1 ? 'Address' : 'Addresses'}!`,
						<FontAwesomeIcon icon={faAddressBook} />,
						ColourMod.Success
					);
				}
			})
			.catch((e) => {
				setAddressError(e.toString());
				addToast('Address Import Failed', <FontAwesomeIcon icon={faAddressBook} />, ColourMod.Alert);
			})
			.finally(() => {
				setAddressSaving(false);
			});
	};

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={formMethods.handleSubmit((data: FieldValues) => HandleSave(data))}>
				<fieldset disabled={addressSaving}>
					<AddressImporterResults
						addressData={props.addressData}
						embeddedForm={props.embeddedForm}
						removeCallback={props.removeCallback}
					/>
				</fieldset>
			</form>
		</FormProvider>
	);
});

export default AddressImporterForm;
